/**
 * Policies v3 — the transport of the external-auditor area (G1-G6), and of the admin's report downloads.
 *
 * HumanFirewall is a human-risk-management and security-awareness-training platform; the Policies module puts an
 * organisation's own policy PDFs in front of its own staff and keeps the proof. An outside auditor, invited by that
 * organisation, reads the proof read-only; the credential is the session token a one-time code earned
 * (lib/policies/audit-session.ts), sent as `X-Policy-Audit-Token`. This file is the only place that header is built.
 *
 * Two doors, two credentials, kept apart on purpose:
 *  - the AUDITOR (`/v1/public/policy-audit/*`): `auditorFetch` / `auditorFetchBlob`. No cookies (`credentials: 'omit'`),
 *    no Authorization header, no workspace header: the grant row names the tenant and nothing the browser sends can
 *    pick another (CONTRACT 0.8). A 401 ends the session in this tab (the page then says so); a 403
 *    `policy_auditor_access_ended` ends it and records that the ACCESS, not just the session, is over.
 *  - the ADMIN (`/v1/policies/audit-reports/*`): `adminAuditBlob`, the admin session + workspace header with one refresh
 *    on a 401, for the internal builder's file downloads. JSON calls of the admin go through `fetchApi` as everywhere.
 *
 * Every refusal arrives as an ApiClientError with `.errorCode`; screens word it with policyErrorMessage and never show
 * `.message`. A file is downloaded through the API and saved from memory: no client-side ZIP, no public URL.
 */
import { API_CONFIG } from '@/lib/api/config'
import { ApiClientError, getAccessToken, getWorkspaceId, refreshAccessToken } from '@/lib/api/client'
import { filenameFromContentDisposition } from '@/lib/http/content-disposition'
import { clearAuditorSession, getAuditorToken, markAuditorAccessEnded } from './audit-session'

export const AUDITOR_TOKEN_HEADER = 'X-Policy-Audit-Token'

const apiUrl = (path: string) => `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${path}`

export interface AuditFileBlob {
  blob: Blob
  mime: string
  fileName: string
}

export interface AuditorFetchInit extends Omit<RequestInit, 'credentials' | 'headers'> {
  /** false for the sign-in routes: they are what earns a token, and must not send an old one. Default true. */
  auth?: boolean
  headers?: Record<string, string>
}

async function throwFor(res: { status: number; statusText: string; json: () => Promise<unknown> }): Promise<never> {
  let body: unknown
  try {
    body = await res.json()
  } catch {
    body = undefined
  }
  throw new ApiClientError(res.status, res.statusText, body)
}

/** What a refusal does to the tab's session, before the caller sees it. */
function applyRefusal(error: ApiClientError): void {
  if (error.statusCode === 401) {
    clearAuditorSession()
    return
  }
  if (error.statusCode === 403 && error.errorCode === 'policy_auditor_access_ended') {
    const body = error.body !== null && typeof error.body === 'object' ? (error.body as { endedAt?: unknown }) : {}
    markAuditorAccessEnded(typeof body.endedAt === 'string' ? body.endedAt : null)
    clearAuditorSession()
  }
}

async function auditorRequest(path: string, init: AuditorFetchInit): Promise<Awaited<ReturnType<typeof fetch>>> {
  const { auth = true, headers: given = {}, ...rest } = init
  const headers: Record<string, string> = { Accept: 'application/json', ...given }
  if (typeof rest.body === 'string' && !headers['Content-Type']) headers['Content-Type'] = 'application/json'
  if (auth) {
    const token = getAuditorToken()
    // No token: the session is over. Say so without a request (the API would say the same, 401).
    if (!token) throw new ApiClientError(401, 'Unauthorized', { statusCode: 401, errorCode: 'policy_auditor_session_expired' })
    headers[AUDITOR_TOKEN_HEADER] = token
  }
  const res = await fetch(apiUrl(path), { ...rest, headers, credentials: 'omit' })
  if (!res.ok) {
    try {
      await throwFor(res)
    } catch (error) {
      if (error instanceof ApiClientError) applyRefusal(error)
      throw error
    }
  }
  return res
}

/** A JSON call as the auditor. A 204 or an empty body reads as `undefined`. */
export async function auditorFetch<T>(path: string, init: AuditorFetchInit = {}): Promise<T> {
  const res = await auditorRequest(path, init)
  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** A file (PDF, CSV, ZIP) as the auditor, with the name the API gave it. */
export async function auditorFetchBlob(path: string, fallbackName: string): Promise<AuditFileBlob> {
  const res = await auditorRequest(path, { headers: { Accept: '*/*' } })
  return blobOf(res, fallbackName)
}

async function blobOf(res: { blob: () => Promise<Blob>; headers: { get: (name: string) => string | null } }, fallbackName: string): Promise<AuditFileBlob> {
  const blob = await res.blob()
  const mime = (res.headers.get('content-type') || blob.type || '').split(';')[0].trim().toLowerCase()
  return { blob, mime, fileName: filenameFromContentDisposition(res.headers.get('content-disposition')) ?? fallbackName }
}

/**
 * A file from the admin side (`/v1/policies/audit-reports/:id/files/:kind`): the admin session and workspace header, one
 * refresh on a 401. The internal report viewer's download; the auditor never uses it.
 */
export async function adminAuditBlob(path: string, fallbackName: string): Promise<AuditFileBlob> {
  const headers: Record<string, string> = {}
  const token = getAccessToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const workspaceId = getWorkspaceId()
  if (workspaceId) headers['x-workspace-id'] = workspaceId
  let res = await fetch(apiUrl(path), { headers })
  if (res.status === 401) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      headers.Authorization = `Bearer ${refreshed}`
      res = await fetch(apiUrl(path), { headers })
    }
  }
  if (!res.ok) await throwFor(res)
  return blobOf(res, fallbackName)
}
